import React from "react";
import {
  DateField,
  List,
  NumberField,
  TextField as RaTextField,
  useSidebarState,
  useTranslate,
} from "react-admin";
import {
  Box,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  CustomDatagrid,
  FilterField,
  ListActionsBar,
} from "@/components/common";

const i18nBase = "resources.statistics";

const ListActions: React.FC = () => {
  const translate = useTranslate();
  return (
    <ListActionsBar
      filters={
        <>
          <FilterField
            source="accountName"
            label={translate(`${i18nBase}.filters.accountName`)}
          />
        </>
      }
    />
  );
};

const datagridSx = {
  "@media (min-width: 600px)": {
    overflowX: "hidden",
    "& .RaDatagrid-tableWrapper": { overflowX: "hidden" },
    "& .MuiTableContainer-root": { overflowX: "hidden", maxWidth: "100%" },
    "& table": {
      tableLayout: "fixed",
      width: "100%",
    },
    "& thead th": {
      whiteSpace: "normal",
      wordBreak: "normal",
      overflowWrap: "break-word",
      lineHeight: 1.3,
      fontSize: 13,
      fontWeight: 600,
      padding: "12px 8px",
      verticalAlign: "top",
    },
    "& tbody td": {
      fontSize: 13,
      padding: "8px",
      wordBreak: "break-word",
    },
    "& .column-accountName": { width: "12%" },
    "& .column-api": { width: "16%" },
    "& .column-lastAccess": { width: "12%" },
    "& .column-lastDaySuccessfulAccess": { maxWidth: "10%" },
    "& .column-lastDayWrongAccess": { width: "10%" },
    "& .column-lastWeekSuccessfulAccess": { width: "10%" },
    "& .column-lastWeekWrongAccess": { width: "10%" },
    "& .column-lastMonthSuccessfulAccess": { width: "10%" },
    "& .column-lastMonthWrongAccess": { width: "10%" },
  },
};

/**
 * Legacy API-audit table (account × endpoint access counters). Lives
 * behind «View all API statistics» in the dashboard.
 */
export const ApiStatisticsTable: React.FC = () => {
  const [sidebarOpen] = useSidebarState();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const maxWidth = isMobile ? "100%" : sidebarOpen ? "85vw" : "95vw";

  return (
    <Box>
      <List
        actions={<ListActions />}
        sort={{ field: "0", order: "ASC" }}
        storeKey={false}
        disableSyncWithLocation
        perPage={10}
        exporter={false}
        sx={{
          maxWidth,
          overflow: { xs: "visible", sm: "hidden" },
          textOverflow: { sm: "ellipsis" },
          whiteSpace: { sm: "nowrap" },
          "& .RaList-main, & .RaList-content": {
            overflow: "visible",
            width: "100%",
            maxWidth: "100%",
          },
          "& .RaListToolbar-root, & .RaList-actions": {
            display: "flex",
            width: "100%",
            mb: { xs: 1, sm: 2 },
            px: 0,
            py: { xs: 0, sm: 1 },
            minHeight: "auto",
          },
        }}
      >
        <CustomDatagrid
          primarySource="accountName"
          bulkActionButtons={false}
          disableMobileView
          sx={datagridSx}
        >
          <RaTextField source="accountName" />
          <RaTextField source="api" />
          <DateField
            source="lastAccess"
            showTime
            options={{
              day: "2-digit",
              month: "short",
              year: "numeric",
              hour: "2-digit",
              minute: "2-digit",
              hour12: false,
            }}
          />
          <NumberField source="lastDaySuccessfulAccess" />
          <NumberField source="lastDayWrongAccess" />
          <NumberField source="lastWeekSuccessfulAccess" />
          <NumberField source="lastWeekWrongAccess" />
          <NumberField source="lastMonthSuccessfulAccess" />
          <NumberField source="lastMonthWrongAccess" />
        </CustomDatagrid>
      </List>
    </Box>
  );
};

export default ApiStatisticsTable;
